import type {
    ICampaignHostCreationAccess,
    ICampaignHostPackage,
    ICampaignHostPaymentRail,
    ICampaignHostPricingResponse,
    ICampaignHostSubscription,
} from '../types/global/campaignHostSubscription.types';

/** `api()` returns axios `response.data` (often `{ status, data }`). */
export function unwrapApiPayload<T = unknown>(response: unknown): T | null {
    if (response == null) return null;
    const r = response as Record<string, unknown>;

    if (r.status === 'success' && r.data !== undefined) {
        return r.data as T;
    }

    const nested = r.data as Record<string, unknown> | undefined;
    if (nested?.status === 'success' && nested.data !== undefined) {
        return nested.data as T;
    }

    if (r.data !== undefined) {
        return r.data as T;
    }

    return response as T;
}

/** GET /campaign-host-subscriptions/mine — subscription doc or `{ subscription: doc | null }`. */
function isCampaignHostSubscriptionLike(value: unknown): value is ICampaignHostSubscription {
    if (value == null || typeof value !== 'object') return false;
    const raw = value as Record<string, unknown>;
    return typeof raw._id === 'string' && typeof raw.status === 'string';
}

export function parseCampaignHostMinePayload(payload: unknown): ICampaignHostSubscription | null {
    if (payload == null) return null;

    if (isCampaignHostSubscriptionLike(payload)) {
        return payload;
    }

    const raw = payload as Record<string, unknown>;
    if ('subscription' in raw) {
        const nested = raw.subscription;
        if (nested == null) return null;
        return isCampaignHostSubscriptionLike(nested) ? nested : null;
    }

    return null;
}

/** Unwrap API body or throw — for sagas that require a payload. */
export function requireApiPayload<T>(response: unknown, message = 'Empty API response'): T {
    const data = unwrapApiPayload<T>(response);
    if (data == null) {
        throw new Error(message);
    }
    return data;
}

export function normalizeCampaignHostPackage(raw: Record<string, unknown>): ICampaignHostPackage {
    const billingTypeRaw = raw.billing_type ?? raw.billingType;
    const interval = (raw.interval as ICampaignHostPackage['interval']) ?? null;

    let billing_type: ICampaignHostPackage['billing_type'] = 'recurring';
    if (billingTypeRaw === 'lifetime' || billingTypeRaw === 'one_time') {
        billing_type = 'lifetime';
    } else if (billingTypeRaw === 'recurring') {
        billing_type = 'recurring';
    }

    return {
        _id: String(raw._id ?? raw.id ?? ''),
        name: String(raw.name ?? 'Plan'),
        slug: String(raw.slug ?? ''),
        description: (raw.description as string) ?? undefined,
        sort_order: Number(raw.sort_order ?? raw.sortOrder ?? 0),
        billing_type,
        list_price_cents: Number(raw.list_price_cents ?? raw.listPriceCents ?? 0),
        features: Array.isArray(raw.features) ? (raw.features as string[]) : [],
        unlimited_campaign_creation: Boolean(raw.unlimited_campaign_creation ?? raw.unlimitedCampaignCreation),
        unlimited_creators: Boolean(raw.unlimited_creators ?? raw.unlimitedCreators),
        max_creators: (raw.max_creators ?? raw.maxCreators ?? null) as number | null,
        stripe_product_id: (raw.stripe_product_id ?? raw.stripeProductId) as string | undefined,
        stripe_price_id: (raw.stripe_price_id ?? raw.stripePriceId) as string | undefined,
        effective_stripe_price_id: (raw.effective_stripe_price_id ?? raw.effectiveStripePriceId) as string | undefined,
        interval,
        unit_amount: (raw.unit_amount ?? raw.unitAmount) as number | null | undefined,
        currency: (raw.currency as string) ?? undefined,
        price_interval: (raw.price_interval ?? raw.priceInterval) as string | undefined,
        formatted_for_display: (raw.formatted_for_display ?? raw.formattedForDisplay) as string | undefined,
        price_source: (raw.price_source ?? raw.priceSource) as ICampaignHostPackage['price_source'],
        paystack_plan: raw.paystack_plan as ICampaignHostPackage['paystack_plan'],
        converted_amount_no_symbol: (raw.converted_amount_no_symbol ?? raw.convertedAmountNoSymbol) as
            | number
            | undefined,
        transaction_currency: (raw.transaction_currency ?? raw.transactionCurrency) as string | undefined,
        rate: (raw.rate as number) ?? undefined,
    };
}

export function extractCampaignHostPackages(payload: unknown): ICampaignHostPackage[] {
    if (payload == null) return [];

    if (Array.isArray(payload)) {
        return payload
            .filter(Boolean)
            .map(item => normalizeCampaignHostPackage(item as Record<string, unknown>))
            .filter(p => p._id);
    }

    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.packages)) {
        return extractCampaignHostPackages(obj.packages);
    }

    if (Array.isArray(obj.Packages)) {
        return extractCampaignHostPackages(obj.Packages);
    }

    return [];
}

/**
 * Parses GET /campaign-host-subscriptions/pricing body:
 * `{ status: "success", data: { packages, ltd_promotion, creation_access } }`
 * (`api()` already returns the axios JSON body.)
 */
export function parseCampaignHostPricingApiResponse(response: unknown): ICampaignHostPricingResponse {
    const envelope = response as { status?: string; data?: unknown } | null;
    if (envelope?.status === 'success' && envelope.data != null) {
        return normalizeCampaignHostPricingPayload(envelope.data);
    }
    return normalizeCampaignHostPricingPayload(unwrapApiPayload(response));
}

export function isCampaignHostLtdPackage(pkg: ICampaignHostPackage): boolean {
    if (pkg.billing_type === 'lifetime') return true;
    const slug = pkg.slug.toLowerCase();
    return slug.includes('ltd') || slug.includes('lifetime');
}

/** Sales-assisted enterprise SKU (`campaign-host-enterprise`). Not self-serve checkout. */
export function isCampaignHostEnterprisePackage(
    pkg: Pick<ICampaignHostPackage, 'slug' | 'unlimited_creators'>,
): boolean {
    const slug = (pkg.slug || '').toLowerCase();
    if (slug === 'campaign-host-enterprise' || slug.includes('enterprise')) {
        return true;
    }
    return Boolean(pkg.unlimited_creators);
}

export function normalizeCampaignHostPaymentRail(raw: unknown): ICampaignHostPaymentRail | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const preferred = String(r.preferred_provider ?? r.preferredProvider ?? 'stripe').toLowerCase();
    const preferred_provider =
        preferred === 'paystack' ? 'paystack' : preferred === 'flutterwave' ? 'flutterwave' : 'stripe';
    return {
        country_code: (r.country_code ?? r.countryCode ?? null) as string | null,
        country_name: (r.country_name ?? r.countryName) as string | undefined,
        is_africa: (r.is_africa ?? r.isAfrica) as boolean | undefined,
        paystack_available: Boolean(r.paystack_available ?? r.paystackAvailable ?? false),
        flutterwave_available: Boolean(r.flutterwave_available ?? r.flutterwaveAvailable ?? false),
        stripe_available: (r.stripe_available ?? r.stripeAvailable) as boolean | undefined,
        preferred_provider,
        use_paystack: Boolean(r.use_paystack ?? r.usePaystack ?? false),
        use_flutterwave: Boolean(r.use_flutterwave ?? r.useFlutterwave ?? false),
        use_stripe: Boolean(r.use_stripe ?? r.useStripe ?? true),
        paystack_currency: (r.paystack_currency ?? r.paystackCurrency ?? null) as string | null,
        flutterwave_currency: (r.flutterwave_currency ?? r.flutterwaveCurrency ?? null) as string | null,
        flutterwave_public_key: (r.flutterwave_public_key ?? r.flutterwavePublicKey ?? null) as string | null,
        payment_currency: (r.payment_currency ?? r.paymentCurrency) as string | undefined,
        wallet_currency: (r.wallet_currency ?? r.walletCurrency) as string | undefined,
        channels: Array.isArray(r.channels) ? (r.channels as string[]) : undefined,
        supports_direct_debit: (r.supports_direct_debit ?? r.supportsDirectDebit) as boolean | undefined,
        fallback_note: (r.fallback_note ?? r.fallbackNote ?? null) as string | null,
    };
}

export function normalizeCampaignHostCreationAccess(raw: unknown, fallbackFeeCents = 0): ICampaignHostCreationAccess {
    const r = (raw ?? {}) as Record<string, unknown>;
    const fee = Number(r.per_campaign_fee_cents ?? r.perCampaignFeeCents ?? fallbackFeeCents);
    const hasMaxKey =
        Object.prototype.hasOwnProperty.call(r, 'max_creators') ||
        Object.prototype.hasOwnProperty.call(r, 'maxCreators');
    const maxRaw = r.max_creators ?? r.maxCreators;
    const max_creators_unlimited =
        Boolean(r.max_creators_unlimited ?? r.maxCreatorsUnlimited) || (hasMaxKey && maxRaw === null);

    let max_creators: number | null = null;
    if (!max_creators_unlimited && maxRaw != null && maxRaw !== '') {
        const n = Number(maxRaw);
        if (Number.isFinite(n) && n > 0) max_creators = Math.floor(n);
    }

    const walletBalance = r.wallet_balance_cents ?? r.walletBalanceCents;
    const shortfall = r.shortfall_cents ?? r.shortfallCents;

    return {
        can_create: r.can_create !== false && r.canCreate !== false,
        unlimited: Boolean(r.unlimited),
        per_campaign_fee_cents: Number.isFinite(fee) ? fee : 0,
        requires_wallet_charge: Boolean(r.requires_wallet_charge ?? r.requiresWalletCharge),
        wallet_balance_cents: walletBalance == null || walletBalance === '' ? undefined : Number(walletBalance),
        shortfall_cents: shortfall == null || shortfall === '' ? undefined : Number(shortfall),
        subscription: (r.subscription as ICampaignHostCreationAccess['subscription']) ?? null,
        max_creators,
        max_creators_unlimited,
    };
}

export function normalizeCampaignHostPricingPayload(payload: unknown): ICampaignHostPricingResponse {
    const raw = (payload ?? {}) as Record<string, unknown>;
    const packages = extractCampaignHostPackages(payload);
    const paymentRailRaw = raw.payment_rail ?? raw.paymentRail;

    const ltdRaw = (raw.ltd_promotion ?? raw.ltdPromotion ?? {}) as Record<string, unknown>;
    const feeCents = Number(raw.per_campaign_fee_cents ?? raw.perCampaignFeeCents ?? 5000);
    const creationRaw = raw.creation_access ?? raw.creationAccess;

    return {
        packages,
        ltd_promotion: {
            enabled: Boolean(ltdRaw.enabled),
            active: Boolean(ltdRaw.active),
            starts_at: (ltdRaw.starts_at ?? ltdRaw.startsAt ?? null) as string | null,
            ends_at: (ltdRaw.ends_at ?? ltdRaw.endsAt ?? null) as string | null,
            stripe_price_id: (ltdRaw.stripe_price_id ?? ltdRaw.stripePriceId ?? null) as string | null,
            promo_price_cents: (ltdRaw.promo_price_cents ?? ltdRaw.promoPriceCents ?? null) as number | null,
        },
        per_campaign_fee_cents: feeCents,
        creation_access: normalizeCampaignHostCreationAccess(
            creationRaw ?? {
                can_create: true,
                unlimited: false,
                per_campaign_fee_cents: feeCents,
                requires_wallet_charge: true,
                subscription: null,
            },
            feeCents,
        ),
        payment_rail: normalizeCampaignHostPaymentRail(paymentRailRaw),
    };
}
